import { useEffect } from 'react';
import { getModelsForAggregation } from '@app/common/static-data/models';
import Logger from '@app/utils/logger';
import { ModelState, MarketAggregatesState } from './types';
import { fetchLatestRunsCached } from './utils';
import { useDemo } from '@app/components/demo/DemoProvider';

export const useModelFetchingOperations = (
  state: MarketAggregatesState,
  setState: React.Dispatch<React.SetStateAction<MarketAggregatesState>>,
) => {
  const { isDemo } = useDemo();

  // Fetch available models when weight type changes
  useEffect(() => {
    const fetchAvailableModels = async () => {
      try {
        setState((prevState) => ({ ...prevState, loading: true, error: null }));

        const filteredModels = getModelsForAggregation(state.weightType);
        const latestRuns = await fetchLatestRunsCached(state.weightType, isDemo);

        setState((prevState) => {
          // Only preserve existing model selections for models that support the new weight type
          const previousModelSelections = new Map(
            prevState.availableModels
              .filter((model) =>
                // Only preserve selections for models that actually support the new weight type
                filteredModels.some((filteredModel) => filteredModel.id === model.id),
              )
              .map((model) => [
                model.id,
                {
                  isSelected: model.isSelected,
                  selectedRuns: model.selectedRuns,
                },
              ]),
          );

          const RETENTION_DAYS = 7; // align with UI default window
          const earliestAllowed = new Date();
          earliestAllowed.setUTCDate(earliestAllowed.getUTCDate() - (RETENTION_DAYS - 1));

          let modelStates: ModelState[] = filteredModels.map((model) => {
            const previousSelection = previousModelSelections.get(model.id);
            const isModelAvailable = !!latestRuns[model.id];
            const latestRunTime = latestRuns[model.id];

            // Validate and filter selected runs for the new weight type
            let validSelectedRuns: { date: string; time: string }[] = [];
            let shouldKeepSelection = false;

            if (isModelAvailable && previousSelection?.selectedRuns && latestRunTime) {
              const latestRunDate = new Date(latestRunTime);
              // Keep only runs not in the future and within a recent retention window
              validSelectedRuns = previousSelection.selectedRuns.filter((run) => {
                const runTime = new Date(`${run.date}T${run.time}:00:00Z`);
                return runTime <= latestRunDate && runTime >= earliestAllowed;
              });

              shouldKeepSelection = validSelectedRuns.length > 0;
            }

            return {
              id: model.id,
              label: model.label,
              isSelected: shouldKeepSelection && (previousSelection?.isSelected || false),
              selectedRuns: validSelectedRuns,
              isAvailable: isModelAvailable,
              latestRun: latestRunTime,
            };
          });

          // In demo mode, always ensure EPT models are selected with at least the latest run
          if (isDemo) {
            const demoModelIds = new Set(['ept1_5', 'ept2', 'ept2_e']);
            modelStates = modelStates.map((m) => {
              if (demoModelIds.has(m.id) && m.isAvailable) {
                // If no runs selected yet, default to latest run
                let selectedRuns = m.selectedRuns;
                if ((!selectedRuns || selectedRuns.length === 0) && m.latestRun) {
                  const d = new Date(m.latestRun);
                  const yyyy = d.getUTCFullYear();
                  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
                  const dd = String(d.getUTCDate()).padStart(2, '0');
                  const hh = String(d.getUTCHours()).padStart(2, '0');
                  selectedRuns = [{ date: `${yyyy}-${mm}-${dd}`, time: hh }];
                }
                return { ...m, isSelected: true, selectedRuns };
              }
              return m;
            });
          }

          const newState = {
            ...prevState,
            availableModels: modelStates,
            loading: false,
            error: null,
          };

          return newState;
        });
      } catch (error: any) {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: error.message || 'Failed to fetch available models',
        }));
        Logger.error('Failed to fetch available models', { error, weightType: state.weightType });
      }
    };

    fetchAvailableModels();
  }, [state.weightType, setState, isDemo]);
};
